import * as XLSX from "xlsx"
import { listItems, valueAtPath } from "@/lib/record-paths"
import { isRecord } from "@/lib/utils"

const SHEET_NAME_LIMIT = 31

/**
 * Turns a pulled CSV into an Excel workbook client-side. Optionally drops columns that
 * are empty in every data row — bulk pulls of tenants with hundreds of unused custom
 * fields otherwise open as a wall of blank columns.
 */
export function csvToWorkbook(csv: string, sheetName: string, dropEmptyColumns: boolean): XLSX.WorkBook {
  const parsed = XLSX.read(csv, { type: "string", raw: true })
  const source = parsed.Sheets[parsed.SheetNames[0]]
  const rows = XLSX.utils.sheet_to_json<unknown[]>(source, { header: 1, blankrows: false, defval: "" })

  const workbook = XLSX.utils.book_new()
  appendSheet(workbook, sheetName, rows, dropEmptyColumns)
  return workbook
}

/** Keeps a column when any data row (below the header) has a value in it; a header-only sheet keeps every column */
function withoutEmptyColumns(rows: unknown[][]): unknown[][] {
  if (rows.length < 2) return rows
  const [header, ...data] = rows
  const keep = header.map((_, column) => data.some((row) => row[column] !== undefined && row[column] !== ""))
  return rows.map((row) => row.filter((_, column) => keep[column]))
}

/** A to-many association written to its own sheet instead of the parent's row */
export interface SheetField {
  /** The association's column key, which is also its path into the record and the sheet's name ("Placement.commissions") */
  key: string
  /** The parent record's id column, written first on every item row ("id", or "Placement.id" in a composite pull); a parent seen again (a related entity shared by several records) is written once */
  parentIdPath: string
  /** Sub-paths selected under the association ("role", "user.id"); every item key when empty */
  columns: string[]
}

/**
 * Lays pulled JSON records out as a workbook: the main sheet has one column per header,
 * flattened the way the server flattens CSV, and each sheet field gets a sheet with one
 * row per item, keyed by the parent's id. On the main sheet a sheet field keeps its
 * count, has its parent's id first when that was not selected, and drops its item columns.
 */
export function recordsToWorkbook(
  records: readonly Record<string, unknown>[],
  headers: readonly string[],
  sheets: readonly SheetField[],
  mainSheetName: string,
  dropEmptyColumns: boolean
): XLSX.WorkBook {
  const flat = records.map((record) => withSheetCounts(record, flattenRecord(record), sheets))
  const itemColumn = (header: string) => sheets.some((sheet) => header.startsWith(`${sheet.key}[]`))
  const parentIds = sheets.map((sheet) => sheet.parentIdPath).filter((path) => !headers.includes(path))
  const mainHeaders = Array.from(new Set([...parentIds, ...headers.filter((header) => !itemColumn(header)), ...sheets.map((sheet) => `${sheet.key}.total`)]))

  const workbook = XLSX.utils.book_new()
  appendSheet(workbook, mainSheetName, [mainHeaders, ...flat.map((row) => mainHeaders.map((header) => row[header] ?? ""))], dropEmptyColumns)
  for (const sheet of sheets) {
    appendSheet(workbook, sheet.key, itemRows(records, flat, sheet), dropEmptyColumns)
  }
  return workbook
}

/** A sheet field's count on the main sheet, from the vendor's total or the items themselves (plain arrays carry none) */
function withSheetCounts(record: Record<string, unknown>, flat: Record<string, unknown>, sheets: readonly SheetField[]): Record<string, unknown> {
  for (const sheet of sheets) {
    const countPath = `${sheet.key}.total`
    if (typeof flat[countPath] !== "number") flat[countPath] = listItems(valueAtPath(record, sheet.key)).length
  }
  return flat
}

function itemRows(records: readonly Record<string, unknown>[], flat: Record<string, unknown>[], sheet: SheetField): unknown[][] {
  const seen = new Set<unknown>()
  const items = records.flatMap((record, index) => {
    const parentId = flat[index][sheet.parentIdPath] ?? ""
    if (parentId !== "" && seen.has(parentId)) return []
    seen.add(parentId)
    return listItems(valueAtPath(record, sheet.key)).map((item) => ({ parentId, values: flattenRecord(asRecord(item)) }))
  })
  const columns = sheet.columns.length > 0 ? sheet.columns : Array.from(new Set(items.flatMap((item) => Object.keys(item.values))))
  return [[sheet.parentIdPath, ...columns], ...items.map((item) => [item.parentId, ...columns.map((column) => item.values[column] ?? "")])]
}

/** Sheet names are unique regardless of case, at most 31 characters, and free of the characters Excel forbids */
function appendSheet(workbook: XLSX.WorkBook, name: string, rows: unknown[][], dropEmptyColumns: boolean): void {
  const base = name.replace(/[:\\/?*[\]]/g, "_").slice(0, SHEET_NAME_LIMIT) || "Sheet1"
  const taken = (candidate: string) => workbook.SheetNames.some((existing) => existing.toLowerCase() === candidate.toLowerCase())
  let candidate = base
  for (let suffix = 2; taken(candidate); suffix++) {
    candidate = `${base.slice(0, SHEET_NAME_LIMIT - 2)}${suffix}`
  }
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(dropEmptyColumns ? withoutEmptyColumns(rows) : rows), candidate)
}

/**
 * Mirrors the server's CSV flattening: nested objects as "parent.child", arrays as
 * compact JSON under "path[]", null as an empty cell. Scalars keep their type so Excel
 * gets numbers, not text.
 */
export function flattenRecord(record: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  const walk = (value: unknown, prefix: string) => {
    if (Array.isArray(value)) {
      result[`${prefix}[]`] = JSON.stringify(value)
    } else if (isRecord(value)) {
      for (const [key, child] of Object.entries(value)) walk(child, prefix === "" ? key : `${prefix}.${key}`)
    } else if (prefix !== "") {
      result[prefix] = value ?? ""
    }
  }
  walk(record, "")
  return result
}

function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : { value }
}

